import React, { Component } from "react";
import { Link } from "react-router-dom";
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';

import { ContainerWrapper, Content } from '../../components/GridComponent/AdminLte.js';
import PageLoading from "../PageLoading";
import Wrapper from "../../components/Template/Wrapper";
import Authenticator from "../../services/Authenticator";
import { Select, Text } from "./BroadcastFormSections";
import { Summary, ResponsesTable, EMPTY_FILTERS, queryFromFilters } from "./broadcastResponsesHelpers";
import {
  fetchBroadcastResponses, exportBroadcastResponses, fetchBroadcastLookups,
} from '../../store/actions/broadcasts/broadcastActions';
import "./Broadcasts.css";

/**
 * Announcements -> Manage -> Responses (a form item). Counts for the current period, filters
 * (country, department, submitted range, question version), one row per response, and the
 * XLSX / CSV export with the same filters (SPEC 19.5 / 19.6).
 *
 * Who sees it: the creator, the creator's department head and Admin (8.1); the server answers
 * 403 to everyone else (HR included) and the page shows its own line for that, as for a 404.
 *
 * The filter options come from /broadcasts/lookups when the viewer may call it (publisher or
 * HR); an Admin who is neither sees "All" only — the server applies the filters either way.
 * submitted_at is shown as the server sends it (UTC); the export converts to the item's zone.
 *
 * The content shape is the server's (BroadcastResponseController.php:117-121): `item`,
 * `form_version` (the selected version), `questions`, then the counts FLAT (`audience`,
 * `completed`, `completion_rate` from ResponseQuery::summary and `responses` = total), `rows`,
 * `page`, `per_page`, `total`, `has_more`. The date filters travel as date_from / date_to
 * (ResponseQuery.php:43-44, 90-94).
 */

export const NO_ACCESS = "You do not have access to the responses for this announcement";
export const NOT_AVAILABLE = "This announcement is not available to you";
export const NO_ROWS = "No responses match these filters";

class BroadcastResponses extends Component {

  state = {
    loaded: false,                  // the first fetch answered
    filters: { ...EMPTY_FILTERS },  // what the inputs show
    applied: {},                    // the query of the last "Apply filters" (without page)
  };

  itemId = () => this.props.params.id;
  mayUseLookups = () => (this.props.user && this.props.user.is_broadcast_publisher === true) || Authenticator.scanLevel("HR");

  componentDidMount() {
    if (this.mayUseLookups()) this.props.fetchBroadcastLookups();
    this.load(1).then(() => this.setState({ loaded: true }));
  }

  load = (page) => this.props.fetchBroadcastResponses(this.itemId(), { ...this.state.applied, page: page });

  setFilter = (key, value) => this.setState({ filters: { ...this.state.filters, [key]: value } });

  onApply = () => {
    const applied = queryFromFilters(this.state.filters);
    this.setState({ applied: applied }, () => this.load(1));
  }

  onExport = (format) => this.props.exportBroadcastResponses(this.itemId(), this.state.applied, format);

  data = () => (this.props.responses && this.props.responses.data) || null;

  renderFilters = (data) => {
    const { lookups } = this.props;
    const { filters } = this.state;
    const all = (list) => [["", "All"]].concat((list || []).map((x) => [String(x.id), x.name]));
    const selected = String(data.form_version || data.item.form_version || 1);
    const versions = [];
    for (let v = 1; v <= Number(data.item.form_version || data.form_version || 1); v++) versions.push([String(v), "Version " + v]);
    return (
      <div className="form-row broadcast-filters">
        <div className="col-md-2"><Select id="filter-country" label="Country" value={filters.country_id} onChange={(v) => this.setFilter("country_id", v)} options={all(lookups ? lookups.countries : [])} /></div>
        <div className="col-md-3"><Select id="filter-department" label="Department" value={filters.department_id} onChange={(v) => this.setFilter("department_id", v)} options={all(lookups ? lookups.departments : [])} /></div>
        <div className="col-md-2"><Text id="filter-from" label="From" type="date" value={filters.date_from} onChange={(v) => this.setFilter("date_from", v)} /></div>
        <div className="col-md-2"><Text id="filter-to" label="To" type="date" value={filters.date_to} onChange={(v) => this.setFilter("date_to", v)} /></div>
        <div className="col-md-2"><Select id="filter-version" label="Version" value={filters.version || selected} onChange={(v) => this.setFilter("version", v)} options={versions} /></div>
        <div className="col-md-1 broadcast-filters-apply"><Button variant="outline-primary" type="button" onClick={this.onApply}>Apply filters</Button></div>
      </div>
    );
  }

  renderReport = (data) => {
    const page = Number(data.page || 1);
    return (
      <>
        <h5 className="broadcast-responses-title">{data.item.title}</h5>
        <Summary summary={data} />
        {this.renderFilters(data)}
        <div className="broadcast-toolbar">
          <div className="btn-group" role="group">
            <Button variant="outline-secondary" type="button" disabled={page <= 1} onClick={() => this.load(page - 1)}>Previous</Button>
            <Button variant="outline-secondary" type="button" disabled={!data.has_more} onClick={() => this.load(page + 1)}>Next</Button>
          </div>
          <div>
            <Button variant="outline-success" type="button" className="mr-1" onClick={() => this.onExport("xlsx")}>Export XLSX</Button>
            <Button variant="outline-success" type="button" onClick={() => this.onExport("csv")}>Export CSV</Button>
          </div>
        </div>
        {data.rows && data.rows.length
          ? <ResponsesTable questions={data.questions} rows={data.rows} anonymous={!!data.item.anonymous} />
          : <p>{NO_ROWS}</p>}
      </>
    );
  }

  renderBody = () => {
    const { responses } = this.props;
    if (!this.state.loaded) return <PageLoading />;
    const data = this.data();
    if (data) return this.renderReport(data);
    const status = responses ? responses.error : null;
    if (status === 403) return <p>{NO_ACCESS}</p>;
    if (status === 404) return <p>{NOT_AVAILABLE}</p>;
    return <p>Couldn't load the responses right now.</p>;
  }

  render() {
    return <Wrapper {...this.props} >
      <ContainerWrapper>
        <Content col="12" title="Announcement responses">
          <p><Link to={global.links.broadcast_manage}>Back to list</Link></p>
          {this.renderBody()}
        </Content>
      </ContainerWrapper>
    </Wrapper>;
  }
}

const mapStateToProps = (state) => {
  return {
    user       : state.user,
    lookups    : state.broadcasts.lookups,
    responses  : state.broadcasts.responses,
  }
}
const mapDispatchToProps = (dispatch) => {
  return {
    fetchBroadcastLookups     : () => dispatch( fetchBroadcastLookups() ),
    fetchBroadcastResponses   : ( id, filters ) => dispatch( fetchBroadcastResponses( id, filters ) ),
    exportBroadcastResponses  : ( id, filters, format ) => dispatch( exportBroadcastResponses( id, filters, format ) ),
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(BroadcastResponses);
